import {ArrowBothIcon, ColumnsIcon, ListUnorderedIcon, ProjectIcon, RowsIcon} from '@primer/octicons-react'
import {Label} from '@primer/react'
import {type ReactNode, useMemo} from 'react'

import {useSetVisibleField, useVisibleFieldIdentifiers} from '../../contexts/views/VisibleFieldsProvider'
import {useFields} from '../../contexts/views/FieldsProvider'
import type {Layout} from '../../utils/saved-view-url-params'
import {getFieldsForCapability} from './get-fields-for-capability'
import {Capability, type FieldDefinition, type FieldsData, FieldIdentifier} from './fields'
import type {FieldOption} from '../view-options/field-option'
import {ViewOptionsMenu} from '../view-options/ViewOptionsMenu'
import {getFieldIcon} from './field-icons'
import {useSavedViewOptions} from './use-saved-view-options'
import styles from './SavedViewOptionsMenu.module.css'

const LAYOUT_OPTIONS = [
  {value: 'list', label: 'List', icon: ListUnorderedIcon},
  {value: 'board', label: 'Board', icon: ProjectIcon},
] satisfies ReadonlyArray<{value: Layout; label: string; icon: typeof ListUnorderedIcon}>

function toFieldOption({identifier, label, icon}: FieldDefinition): FieldOption {
  return {identifier, label, icon: getFieldIcon(icon)}
}

/** Sort's leading visual, matching memex: a two-way arrow rotated to point up/down. */
function SortByIcon({size}: {size?: number}) {
  return <ArrowBothIcon size={size} className={styles.sortIcon} />
}

// The repository scaffold still groups by label. Its wrapper exposes no axis writers.
const TEMP_GROUP_BY = FieldIdentifier.LABEL
const noopSelect = () => {}

/**
 * The issues saved-view options menu. Projects the view definition into groupable and
 * attributable field lists; layout comes from the URL via {@link useSavedViewOptions}.
 */
export function SavedViewOptionsMenu() {
  const view = useFields()
  return <SavedViewOptions fields={view} />
}

interface SavedViewOptionsProps {
  fields: FieldsData | undefined
  fieldsStatus?: ReactNode
}

export function SavedViewOptions({fields: view, fieldsStatus}: SavedViewOptionsProps) {
  const {layout, setLayout} = useSavedViewOptions()
  const visibleFieldIdentifiers = useVisibleFieldIdentifiers()
  const setVisibleField = useSetVisibleField()
  const groupFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.GROUPABLE).map(toFieldOption) : []),
    [view],
  )
  const attributeFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.ATTRIBUTABLE).map(toFieldOption) : []),
    [view],
  )
  const sortFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.SORTABLE).map(toFieldOption) : []),
    [view],
  )

  return (
    <ViewOptionsMenu triggerLabel="View options" title="View options">
      <ViewOptionsMenu.Layout
        label="Layout"
        trailing={layout === 'board' ? <Label variant="success">Alpha</Label> : undefined}
        options={LAYOUT_OPTIONS}
        value={layout}
        onSelect={setLayout}
      />

      {layout === 'board' && (
        <>
          <ViewOptionsMenu.Group label="Configuration" status={fieldsStatus}>
            {view && (
              <>
                <ViewOptionsMenu.SingleSelect
                  icon={ColumnsIcon}
                  label="Group by"
                  placeholder="None"
                  fields={groupFields}
                  value={TEMP_GROUP_BY}
                  disabled
                  onSelect={noopSelect}
                />
                <ViewOptionsMenu.SingleSelect
                  icon={RowsIcon}
                  label="Swimlane by"
                  placeholder="None"
                  fields={groupFields}
                  value={null}
                  disabled
                  onSelect={noopSelect}
                />
                <ViewOptionsMenu.SingleSelect
                  icon={SortByIcon}
                  label="Sort by"
                  placeholder="None"
                  fields={sortFields}
                  value={null}
                  disabled
                  onSelect={noopSelect}
                />
              </>
            )}
          </ViewOptionsMenu.Group>
          {view && (
            <ViewOptionsMenu.ToggleGroup
              label="Attributes"
              fields={attributeFields}
              selectedIds={visibleFieldIdentifiers}
              onToggle={setVisibleField}
            />
          )}
        </>
      )}
    </ViewOptionsMenu>
  )
}
